import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Clock3, ArrowUpRight } from "lucide-react";
import { useContent } from "../lib/content";

export const Footer = () => {
  const { t } = useContent();

  return (
    <footer id="visit" className="bg-[#1d1d1b] text-[#eae4da]">
      <div className="max-w-6xl mx-auto px-6 py-20 grid grid-cols-1 lg:grid-cols-2 gap-14">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="font-display font-extrabold tracking-tight text-2xl">
            MAS Arts<span className="text-[#eac119]">.</span>
          </p>
          <p className="text-[10px] font-bold tracking-[0.28em] uppercase text-[#eae4da]/40 mt-1">
            Creative Workshop Lounge
          </p>
          <h2 className="font-display font-extrabold tracking-[-0.02em] text-3xl sm:text-4xl mt-8 leading-[1.12] whitespace-pre-line">
            {t("visit_heading")}
          </h2>
          <p className="mt-5 max-w-md text-[#eae4da]/60 leading-relaxed font-medium">
            {t("visit_text")}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              data-testid="footer-map-link"
              href="https://www.google.com/maps/search/?api=1&query=MAS+Arts+Studio+817+New+York+Ave+Sheboygan+WI"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-bold"
            >
              <MapPin size={16} />
              Get directions
            </a>
            <a
              data-testid="footer-more-about-link"
              href="https://masarts.link"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-[#eae4da]/25 px-6 py-3.5 text-sm font-bold hover:bg-[#eae4da] hover:text-[#1d1d1b] transition-all duration-300"
            >
              masarts.link
              <ArrowUpRight size={15} />
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="grid gap-4 content-start"
        >
          <a
            data-testid="footer-phone-link"
            href="tel:+19203775900"
            className="flex items-center gap-4 rounded-3xl border border-[#eae4da]/12 bg-[#eae4da]/[0.04] p-5 hover:border-[#eac119]/60 transition-all duration-300"
          >
            <span className="w-11 h-11 rounded-2xl bg-[#eac119] text-[#1d1d1b] flex items-center justify-center shrink-0">
              <Phone size={17} />
            </span>
            <span>
              <span className="block text-[10px] font-bold tracking-[0.18em] uppercase text-[#eae4da]/40">Call or text</span>
              <span className="font-display font-extrabold tracking-tight">(920) 377-5900</span>
            </span>
          </a>
          <a
            data-testid="footer-email-link"
            href="mailto:masartsoutreach@gmail.com"
            className="flex items-center gap-4 rounded-3xl border border-[#eae4da]/12 bg-[#eae4da]/[0.04] p-5 hover:border-[#eac119]/60 transition-all duration-300"
          >
            <span className="w-11 h-11 rounded-2xl bg-[#eae4da] text-[#1d1d1b] flex items-center justify-center shrink-0">
              <Mail size={17} />
            </span>
            <span>
              <span className="block text-[10px] font-bold tracking-[0.18em] uppercase text-[#eae4da]/40">Email</span>
              <span className="font-display font-extrabold tracking-tight text-sm sm:text-base">masartsoutreach@gmail.com</span>
            </span>
          </a>
          <div className="flex items-center gap-4 rounded-3xl border border-[#eae4da]/12 bg-[#eae4da]/[0.04] p-5">
            <span className="w-11 h-11 rounded-2xl border border-[#eae4da]/20 text-[#eae4da]/70 flex items-center justify-center shrink-0">
              <Clock3 size={17} />
            </span>
            <span>
              <span className="block text-[10px] font-bold tracking-[0.18em] uppercase text-[#eae4da]/40">Hours</span>
              <span className="font-display font-extrabold tracking-tight">Workshop evenings · by booking only</span>
            </span>
          </div>
        </motion.div>
      </div>

      <div className="border-t border-[#eae4da]/10">
        <div className="max-w-6xl mx-auto px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-[#eae4da]/40 font-medium">© {new Date().getFullYear()} MAS Arts Studio · Sheboygan, WI</p>
          <div className="flex items-center gap-5">
            <p className="font-editorial text-sm text-[#eae4da]/50">
              Paint loud. Leave lighter.
            </p>
            <a
              data-testid="footer-studio-login-link"
              href="/admin"
              className="text-xs font-bold text-[#eae4da]/40 hover:text-[#eac119] transition-colors"
            >
              Studio Login
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};
